import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, FileText } from 'lucide-react'
import { MAX_GOLD_EXAMPLES, type ClassGoldExample } from '@sophia/shared-schemas'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { EmptyState } from '@/components/ui/EmptyState'
import { classesApi, classesQueryKey } from '../api'
import { extractApiError, type NormalizedError } from '@/lib/api'

interface Props {
  classId: string
  examples: ClassGoldExample[]
}

export function GoldExamplesEditor({ classId, examples }: Props) {
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const [externalId, setExternalId] = useState('')
  const [cnj, setCnj] = useState('')
  const [error, setError] = useState<NormalizedError | null>(null)
  const [addOpen, setAddOpen] = useState(examples.length === 0)

  const limit = MAX_GOLD_EXAMPLES
  const reachedLimit = examples.length >= limit

  const addMut = useMutation({
    mutationFn: () =>
      classesApi.addExample(classId, {
        text: text.trim(),
        externalId: externalId.trim() || undefined,
        cnj: cnj.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: classesQueryKey.detail(classId) })
      setText('')
      setExternalId('')
      setCnj('')
      setError(null)
      setAddOpen(false)
    },
    onError: (err) => setError(extractApiError(err)),
  })

  const fieldErrors = error?.fieldErrors ?? {}

  const removeMut = useMutation({
    mutationFn: (id: string) => classesApi.removeExample(classId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: classesQueryKey.detail(classId) }),
  })

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <span className="text-sm text-fg-2">
            <span className="data-mono font-bold">{examples.length}</span>
            <span className="text-fg-3"> / {limit} exemplos-padrão</span>
          </span>
          <p className="text-xs text-fg-3 mt-0.5">
            Publicações 100% certas dessa classe. Cada regra gerada é validada contra eles.
          </p>
        </div>
        {!addOpen && !reachedLimit && (
          <Button variant="secondary" size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" />
            Adicionar exemplo
          </Button>
        )}
      </div>

      {addOpen && (
        <div className="rounded-lg border border-border bg-bg-surface p-5 flex flex-col gap-4">
          <Field
            label="Texto da publicação"
            required
            hint={`Cole o texto inteiro (mín. 20 chars · atual: ${text.trim().length})`}
            error={fieldErrors.text?.[0]}
          >
            <Textarea
              rows={6}
              placeholder="Cole a publicação completa aqui…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              invalid={!!fieldErrors.text}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="ID externo" hint="Opcional — ID_PUBLICACAO da base" error={fieldErrors.externalId?.[0]}>
              <Input
                value={externalId}
                onChange={(e) => setExternalId(e.target.value)}
                invalid={!!fieldErrors.externalId}
              />
            </Field>
            <Field label="Número CNJ" hint="Opcional" error={fieldErrors.cnj?.[0]}>
              <Input
                value={cnj}
                onChange={(e) => setCnj(e.target.value)}
                placeholder="0000000-00.0000.0.00.0000"
                invalid={!!fieldErrors.cnj}
              />
            </Field>
          </div>
          {error && (
            <div className="text-sm text-danger bg-danger-bg border border-danger/30 rounded-md px-3 py-2">
              <strong className="block">{error.message}</strong>
              {Object.entries(fieldErrors).length > 0 && (
                <ul className="mt-1 list-disc list-inside text-xs">
                  {Object.entries(fieldErrors).map(([field, msgs]) => (
                    <li key={field}>
                      <span className="data-mono">{field}</span>: {msgs.join(', ')}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setAddOpen(false)} disabled={addMut.isPending}>
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={() => addMut.mutate()}
              loading={addMut.isPending}
              disabled={text.trim().length < 20}
            >
              Adicionar
            </Button>
          </div>
        </div>
      )}

      {examples.length === 0 && !addOpen ? (
        <EmptyState
          icon={FileText}
          title="Nenhum exemplo-padrão ainda"
          description="Adicione publicações que você tem certeza absoluta que pertencem a essa classe."
          action={
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" />
              Adicionar exemplo
            </Button>
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {examples.map((ex, i) => (
            <li
              key={ex.id}
              className="rounded-md border border-border-subtle bg-bg-surface p-4 flex flex-col gap-2"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 text-2xs">
                  <span className="data-mono font-bold text-fg-2">#{i + 1}</span>
                  {ex.externalId && (
                    <span className="data-mono text-fg-3">id: {ex.externalId}</span>
                  )}
                  {ex.cnj && <span className="data-mono text-fg-3">cnj: {ex.cnj}</span>}
                </div>
                <button
                  type="button"
                  onClick={() => removeMut.mutate(ex.id)}
                  className="text-fg-3 hover:text-danger transition-colors"
                  aria-label="Remover"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <p className="text-sm text-fg-2 whitespace-pre-wrap line-clamp-4 leading-relaxed">{ex.text}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
